import classNames from "classnames";
import { AudioRecorder } from "components/AudioRecorder/AudioRecorder";
import { Option } from "components/Option/Option";
import { ORGANIZATIONS, QUESTION_TYPE } from "constant";
import { useCallback, useMemo } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { FreeText } from "./FreeText/FreeText";
import { GC } from "services/gamecentreService";
import { Button } from "components/Button/Button";
import { storage } from "services/storage";
import { shuffleArr } from "utils/helper";
import { useIsMobileDevice } from "utils/hooks";
import { useLanguageContext } from "store/LanguageProvider";

export const QuestionInput = ({
  type,
  questionId,
  options = [],
  showGridView = false,
  isOptionTextLong,
  onSubmit,
  isQuesMCQWithImageOptions,
  time,
  timeToSolve,
  isOffline,
  isOptionUpperCase = false,
  randomizeOptions = false,
  isNextBtnVisible = true,
  setPauseTimer = () => { },
}) => {

  // ===================
  // useStates
  // ===================
  const [webCamKey, setWebCamKey] = useState(0);
  const [deviceId, setDeviceId] = useState(null);
  const [devices, setDevices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const { rtlView, direction } = useLanguageContext();

  const isMobile = useIsMobileDevice()

  const inIframe = window.location !== window.parent.location;
  const isMcq = type === QUESTION_TYPE.MCQ
  const spotlightOrgFlag = storage.get.org() === ORGANIZATIONS.SPOTLIGHT

  // ===================
  // useEffects
  // ===================
  useEffect(() => {
    if ([
      QUESTION_TYPE.SPEECH_SCRIPTED,
      QUESTION_TYPE.SPEECH_SPONTANEOUS,
    ].includes(type)) {
      navigator.mediaDevices.enumerateDevices().then(handleDevices);
    }
  }, [])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // System has woken up from sleep mode, so force a refresh of the Webcam component
        setWebCamKey(webCamKey + 1)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    };
  }, [webCamKey])

  useEffect(() => {
    let timeout1, timeout2, timeout3
    if (selectedOption) {
      if (isMcq && spotlightOrgFlag) {
        const mcqContainer = document.querySelector(".spotlight-mcq-container")
        const mcqCard = document.querySelector(".ques-card")
        timeout3 = setTimeout(() => {
          mcqContainer.scrollTo({
            top: 0,
            behavior: "smooth"
          })
        }, 150)
        timeout1 = setTimeout(() => {
          mcqCard.classList.add("ques-card-fade-out")
        }, 300)
        timeout2 = setTimeout(() => {
          handleOptionSubmission()
        }, 650)
      }
    }
    return () => {
      clearTimeout(timeout1)
      clearTimeout(timeout2)
      clearTimeout(timeout3)
    }
  }, [selectedOption])

  // ===================
  // functions
  // ===================

  const mcqOptions = useMemo(() => {
    return randomizeOptions ? shuffleArr(options) : options
  }, [options, randomizeOptions])

  const saveWebCamDevice = useCallback((deviceId) => {
    setDeviceId(deviceId)
  }, []);

  // Handle audio submissions separately -> Post message comms
  // For game loaded through url -> Call onSubmit directly
  const handleAudioSubmit = (mediaFile) => {
    setLoading(true);
    setPauseTimer(true)
    if (!inIframe) {
      onSubmit({ mediaId: "" });
      return;
    }
    GC.sendGameDataSaveMessage({
      mediaFile,
      questionId,
      saveAudioAnswer: true
    })
  };

  const handleOptionSelection = (option) => {
    if (loading) return;
    setSelectedOption(option);
  }

  /*
  * onSubmit -> Params in the following order
  * 
  * isMCQ param -> Triggers event from GC to handle mcq save and next question call
  * 
  * onSubmit({ ...option }, nextQuestion = true, markQuizComplete = false, isMCQ = true) 
  */
  const handleOptionSubmission = () => {
    setLoading(true);
    onSubmit(selectedOption, true, false, true);
  };

  const handleDevices = useCallback(
    (mediaDevices) => {
      const videoSources = mediaDevices.filter(
        ({ kind, label }) =>
          kind === 'videoinput' && !label.toLowerCase().includes('back')
      );
      setDevices(videoSources);

      if (!deviceId) {
        const [firstSource] = videoSources ?? [{}];
        saveWebCamDevice(firstSource.deviceId);
      }
    },
    [deviceId, saveWebCamDevice]
  );

  return (
    <>
      {type === QUESTION_TYPE.MCQ ? (
        <>
          <div className={classNames("flex items-center  sm:sticky sm:top-[-1px] bg-[#fff] sm:py-[8px] sm:p-[2px]", {
            "sm:justify-between justify-end sm:w-[unset] w-full": rtlView,
            "justify-between": !rtlView
          })}>
            <p
              className={classNames(
                `text-sm font-normal leading-[26px] text-cocoa-brown`,
                "sm:text-xl sm:leading-9",
                // {
                //   "sm:hidden": isOptionTextLong(),
                // },
                {
                  "hidden": (spotlightOrgFlag && isMcq)
                }
              )}
            >
              Choose the right option
            </p>
            <div className={classNames("flex flex-col items-center justify-between", {
              "hidden": isMcq && spotlightOrgFlag,
              "fixed bottom-0 left-0 bg-[#fff] w-full flex justify-center items-end py-1 opacity-1 transition-opacity": isMobile && (isNextBtnVisible || selectedOption),
              "opacity-0": isMobile && !isNextBtnVisible && !selectedOption,
            })}>
              <Button
                className={`bg-cocoa-brown text-white`}
                onClick={handleOptionSubmission}
                disabled={!selectedOption || loading}
              >
                {!loading ? 'Next' : 'Submitting...'}
              </Button>
            </div>
          </div>
          <div
            dir="auto"
            className={isQuesMCQWithImageOptions ? classNames("mt-2 grid grid-cols-6 grid-rows-1 gap-[10px]") : classNames("mt-2", "", {
              "grid grid-cols-2 grid-rows-2 gap-[10px]":
                showGridView && !isOptionTextLong(),
              "flex flex-col gap-4": !(showGridView && !isOptionTextLong()),
              "w-full": isMcq && spotlightOrgFlag,
              "pb-[52px]": isMobile && !spotlightOrgFlag
            })}
          >
            {mcqOptions.map((option) => (
              <Option
                {...option}
                key={option.id}
                onOptionClick={handleOptionSelection}
                isOptionTextLong={isOptionTextLong}
                isQuesMCQWithImageOptions={isQuesMCQWithImageOptions}
                selected={selectedOption?.optionId === option.id}
                isMcq={isMcq}
                spotlightOrgFlag={spotlightOrgFlag}
                isOptionUpperCase={isOptionUpperCase}
              />
            ))}
          </div>
        </>
      ) : null}
      {type === QUESTION_TYPE.FREE_TEXT ? (
        <FreeText onSubmit={onSubmit} time={time} questionId={questionId} />
      ) : null}
      {[
        QUESTION_TYPE.SPEECH_SCRIPTED,
        QUESTION_TYPE.SPEECH_SPONTANEOUS,
      ].includes(type) && (
          <>
            <AudioRecorder
              questionType={type}
              onDataAvailable={(file) =>
                handleAudioSubmit({
                  file, fileName: file.name, type: "audio"
                })
                // onSubmit({
                //   mediaFile: { file, fileName: file.name, type: "audio" },
                // })
              }
              gameTime={time}
              timeToSolve={timeToSolve}
              loading={loading}
              isOffline={isOffline}
            />
          </>
        )}
    </>
  );
};
